import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../../axiosinterceptor"; // adjust path to your actual axios setup

export const RULE_MANAGEMENT_ACTIONS = {
  SAVE_DRAFT: "SAVE_DRAFT",
  SUBMIT_FOR_TESTING: "SUBMIT_FOR_TESTING",
  SAVE_AND_TEST: "SAVE_AND_TEST",
  SAVE: "SAVE",
  RE_TEST: "RE_TEST",
};


function extractArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.options)) return payload.options;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

// ---------- Thunks ----------

export const fetchNextRuleId = createAsyncThunk(
  "createNewRule/fetchNextRuleId",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/get_next_rule_id",{});
      return response.data; // { next_rule_id: "GDSR0014" }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch next rule ID");
    }
  }
);

// payload: { search_code, search_description, code_type, page_number, page_size }
export const fetchPathwayOptions = createAsyncThunk(
  "createNewRule/fetchPathwayOptions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/path_entry_point_options", payload);
      return response.data; // array of { code_type, code, description }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch pathway options");
    }
  }
);

// payload: { search_code, search_description, page_number, page_size }
export const fetchTriggerCodeOptions = createAsyncThunk(
  "createNewRule/fetchTriggerCodeOptions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/trigger_codes_options", payload);
      return response.data; // array of { code_type, code, description }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch trigger codes");
    }
  }
);

export const submitRuleManagement = createAsyncThunk(
  "createNewRule/submitRuleManagement",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/code_assist_rule_management", payload);
      return response.data; // { rule_id, description, message }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to submit rule");
    }
  }
);
export const fetchEnvironmentOptions = createAsyncThunk(
  "createNewRule/fetchEnvironmentOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/environment_options");
      return response.data; // { environments: ["DEV", "DEMO", ...] }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch environment options"
      );
    }
  }
);
// ---------- Slice ----------

const initialState = {
  nextRuleId: "",
  ruleIdLoading: false,
  ruleIdError: null,

  environmentOptions: [],
  environmentLoading: false,
  environmentError: null,

  pathwayOptions: [],
  pathwayLoading: false,
  pathwayError: null,
  pathwayPage: 1,
  pathwayHasMore: true,

  triggerCodeOptions: [],
  triggerCodeLoading: false,
  triggerCodeError: null,
  triggerCodePage: 1,
  triggerCodeHasMore: true,

  submission: {
    loading: false,
    error: null,
    data: null, // { rule_id, description, message }
    lastAction: null,
  },

};

const CreateNewRuleSlicer = createSlice({
  name: "createNewRule",
  initialState,
  reducers: {
    clearPathwayOptions(state) {
      state.pathwayOptions = [];
      state.pathwayPage = 1;
      state.pathwayHasMore = true;
    },
    clearTriggerCodeOptions(state) {
      state.triggerCodeOptions = [];
      state.triggerCodePage = 1;
      state.triggerCodeHasMore = true;
    },
    resetSubmissionState(state) {
      state.submission = initialState.submission;
    },
    resetCreateNewRuleState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNextRuleId.pending, (state) => {
        state.ruleIdLoading = true;
        state.ruleIdError = null;
      })
      .addCase(fetchNextRuleId.fulfilled, (state, action) => {
        state.ruleIdLoading = false;
        state.nextRuleId = action.payload?.next_rule_id || "";
      })
      .addCase(fetchNextRuleId.rejected, (state, action) => {
        state.ruleIdLoading = false;
        state.ruleIdError = action.payload || action.error.message;
      })

      // pathway options - appends when page_number > 1 (infinite scroll),
      // replaces on page_number 1 (a new search).
      .addCase(fetchPathwayOptions.pending, (state) => {
        state.pathwayLoading = true;
        state.pathwayError = null;
      })
      .addCase(fetchPathwayOptions.fulfilled, (state, action) => {
        const items = extractArray(action.payload);
        const pageNumber = action.meta.arg?.page_number || 1;
        const pageSize = action.meta.arg?.page_size || items.length;
        state.pathwayLoading = false;
        state.pathwayOptions = pageNumber === 1 ? items : [...state.pathwayOptions, ...items];
        state.pathwayPage = pageNumber;
        state.pathwayHasMore = items.length > 0 && items.length === pageSize;
      })
      .addCase(fetchPathwayOptions.rejected, (state, action) => {
        const pageNumber = action.meta.arg?.page_number || 1;
        state.pathwayLoading = false;
        state.pathwayError = action.payload || action.error.message;
        state.pathwayHasMore = false;
        if (pageNumber === 1) {
          state.pathwayOptions = [];
        }
      })

      // trigger code options - same append/replace pattern as pathway options.
      .addCase(fetchTriggerCodeOptions.pending, (state) => {
        state.triggerCodeLoading = true;
        state.triggerCodeError = null;
      })
      .addCase(fetchTriggerCodeOptions.fulfilled, (state, action) => {
        const items = extractArray(action.payload);
        const pageNumber = action.meta.arg?.page_number || 1;
        const pageSize = action.meta.arg?.page_size || items.length;
        state.triggerCodeLoading = false;
        state.triggerCodeOptions = pageNumber === 1 ? items : [...state.triggerCodeOptions, ...items];
        state.triggerCodePage = pageNumber;
        state.triggerCodeHasMore = items.length > 0 && items.length === pageSize;
      })
      .addCase(fetchTriggerCodeOptions.rejected, (state, action) => {
        const pageNumber = action.meta.arg?.page_number || 1;
        state.triggerCodeLoading = false;
        state.triggerCodeError = action.payload || action.error.message;
        state.triggerCodeHasMore = false;
        if (pageNumber === 1) {
          state.triggerCodeOptions = [];
        }
      })

      .addCase(submitRuleManagement.pending, (state, action) => {
        state.submission.loading = true;
        state.submission.error = null;
        state.submission.lastAction = action.meta.arg?.action || null;
      })
      .addCase(submitRuleManagement.fulfilled, (state, action) => {
        state.submission.loading = false;
        state.submission.data = action.payload || null;
      })
      .addCase(submitRuleManagement.rejected, (state, action) => {
        state.submission.loading = false;
        state.submission.error = action.payload || action.error.message;
        state.submission.data = null;
      })
      
      .addCase(fetchEnvironmentOptions.pending, (state) => {
        state.environmentLoading = true;
        state.environmentError = null;
      })
      .addCase(fetchEnvironmentOptions.fulfilled, (state, action) => {
        state.environmentLoading = false;
        state.environmentOptions = action.payload?.environments || [];
      })
      .addCase(fetchEnvironmentOptions.rejected, (state, action) => {
        state.environmentLoading = false;
        state.environmentError =
          action.payload || action.error.message;
        state.environmentOptions = [];
      })
  },
});

export const {
  clearPathwayOptions,
  clearTriggerCodeOptions,
  resetSubmissionState,
  resetCreateNewRuleState,
} = CreateNewRuleSlicer.actions;

export default CreateNewRuleSlicer.reducer;